import React from 'react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import { slugify } from '../../../../../../Shared/Frontend/utils/slugify'
import { htmlDecode, nl2br, unEntity } from '../../../utils/html'
import type { ActionProps, ImageDirectionProps, ImageSizesProps } from '../../modernBlocks.state'
import { SharedBlockLayout } from '../shared/sharedBlock.layout'
import type { ImageTextBlockClassNames } from './imageTextBlock.state'

export const imageTextBlockClasses: ImageTextBlockClassNames = {
  // DEFAULTS
  block: 'block block--textimage',
  inview: 'inview',
  inner: 'block__inner ls-container--l ls-gutter ls-spacing--s',
  header: 'block__header',
  label: 'block__label text--s',
  title: 'block__title title--m',
  content: 'block__content',
  text: 'block__text text--styled',
  intro: 'block__intro text--l',
  visual: 'block__visual',
  image: 'block__image',
  footer: 'block__footer ls-clickables',
  clickableWrapper: 'block__action ls-clickable',
  clickablePrimary: 'block__button button button--primary',
  clickableSecondary: 'block__button button button--tertiary',

  // BLOCK SPECIFIC
  blockLeft: 'block--textimage-left',
  blockRight: 'block--textimage-right',
  layoutLeft: 'block__layout',
  layoutRight: 'block__layout',
}

export const ImageTextBlockLayout = {
  Inner: (props: {
    amountNested: number
    children?: React.ReactNode
    imageDirection: ImageDirectionProps
    displayCompact: boolean
  }): JSX.Element => {
    const { amountNested, children, imageDirection, displayCompact } = props
    return (
      <div className={displayCompact == true ? 'block__outer ls-container' : 'block__outer ls-container--xl-nogut'}>
        <div className={getClassNameModern(imageTextBlockClasses, 'inner', 'mw-container-m mx-auto px-gutter')}>
          <div
            className={getClassNameModern(
              imageTextBlockClasses,
              imageDirection == 'left' ? 'layoutLeft' : 'layoutRight',
              'row'
            )}
          >
            {children}
          </div>
        </div>
      </div>
    )
  },
  Content: (
    props: {
      label?: string
      title?: string
      intro?: string
      text: string
      showActionAsLink: boolean
      displayCompact: boolean
    } & ActionProps
  ) => {
    const { label, title, intro, text, showPrimary, showSecondary, primaryAction, secondaryAction, showActionAsLink, displayCompact } = props
    return (
      <>
        <div className={getClassNameModern(imageTextBlockClasses, 'content', 'col-12 col-md-6')}>
          {(title || label || intro) && (
            <header className={getClassNameModern(imageTextBlockClasses, 'header', '')}>
              {label && (
                <p className={getClassNameModern(imageTextBlockClasses, 'label', 'fs-6 text-primary text-uppercase')}>
                  {label}
                </p>
              )}
              {title && (
                <h2
                  id={slugify(htmlDecode(title))}
                  className={getClassNameModern(imageTextBlockClasses, 'title', 'h2 mb-3')}
                >
                  {title}
                </h2>
              )}
              {intro &&
                <div
                  className={getClassNameModern(imageTextBlockClasses, 'intro', 'text--styled mb-3')}
                  dangerouslySetInnerHTML={{ __html: unEntity(intro) }}
                />
              }
            </header>
          )}

          {text && (
            <div
              className={getClassNameModern(imageTextBlockClasses, 'text', 'text--styled')}
              dangerouslySetInnerHTML={{ __html: unEntity(text) }}
            />
          )}

          {(showPrimary || showSecondary) && (
            <footer className={getClassNameModern(imageTextBlockClasses, 'footer', 'pt-5')}>
              {showPrimary && primaryAction && primaryAction.title && primaryAction.url && (
                <div
                  className={getClassNameModern(imageTextBlockClasses, 'clickableWrapper', 'd-block d-sm-inline-block')}
                >
                  <SharedBlockLayout.Clickable
                    type='button'
                    id={`imageTextPrimaryAction-${primaryAction.url}`}
                    link={primaryAction}
                    classNames={getClassNameModern(imageTextBlockClasses, showActionAsLink === true ? 'clickableSecondary' : 'clickablePrimary', 'btn btn-primary btn-sm')}
                  />
                </div>
              )}
              {showSecondary && secondaryAction && secondaryAction.title && secondaryAction.url && (
                <div
                  className={getClassNameModern(
                    imageTextBlockClasses,
                    'clickableWrapper',
                    'd-block d-sm-inline-block ms-sm-3 mt-3 mt-sm-0'
                  )}
                >
                  <SharedBlockLayout.Clickable
                    type='button'
                    id={`imageTextSecondaryAction-${secondaryAction.url}`}
                    link={secondaryAction}
                    classNames={getClassNameModern(imageTextBlockClasses, 'clickableSecondary', 'btn btn-outline-primary btn-sm')}
                  />
                </div>
              )}
            </footer>
          )}
        </div>
      </>
    )
  },
  Visual: (props: {
    image?: ImageSizesProps['image'];
    imageDirection: ImageDirectionProps;
    displayCompact: boolean
 }) => {
    const { image, imageDirection, displayCompact } = props
    return (
      <>
        {image && (
          <div
            className={getClassNameModern(
              imageTextBlockClasses,
              'visual',
              `${`col-12 col-md-6 ${
                imageDirection == 'left' ? 'order-first mb-5 mb-md-0 ' : 'order-last mt-5 mt-md-0 '
              }`}`
            )}
          >
            {displayCompact == false
              ? <>
                  <div className='block__visual-inner fit-picture'>
                    <SharedBlockLayout.ModernImageSrcSet
                      image={image}
                      lazyload={true}
                      priority={undefined}
                      classNames={getClassNameModern(imageTextBlockClasses, 'image', 'w-100')}
                      pictureClassNames='block__picture'
                      imageProportions={{
                        width: '878',
                        height: '532',
                        sizes: '(min-width: 1920px) 878px, (min-width: 768px) 48vw, 100vw'
                      }}
                    />
                  </div>

                  {imageDirection == 'left'
                    ? <svg className="block__mask" width="1053" height="637" viewBox="0 0 1053 637" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fillRule="evenodd" clipRule="evenodd" d="M0.00195312 615.944C105.27 627.662 211.396 632.97 317.8 631.75H324.718C567.191 632.243 808.534 598.689 1041.68 532.071L903.271 4.60669C713.521 59.9642 516.839 87.9337 319.183 87.6708C212.14 88.7121 105.452 81.6845 0.00195312 66.7689V-0.000976562H1052V636.999H0.00195312V615.944Z" fill="white"/></svg>
                    : <svg className="block__mask" width="1053" height="637" viewBox="0 0 1053 637" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fillRule="evenodd" clipRule="evenodd" d="M1052 -0.000976562H0.00195312V636.999H1052V570.911C944.82 555.474 836.343 548.19 727.5 549.249C529.843 548.986 333.162 576.956 143.411 632.313L5.00195 104.849C238.149 38.231 479.492 4.67738 721.964 5.17016H728.883C837.08 3.92944 944.99 9.43876 1052 21.5735V-0.000976562Z" fill="white"/></svg>
                  }
                  {imageDirection == 'left'
                    ? <svg className="block__swoosh" height="1259" viewBox="0 0 2337 1259" width="2337" xmlns="http://www.w3.org/2000/svg"><path d="m1602.8 1253.75c-370.41 4.25-737.457-70.62-1076.628-219.59-339.17-148.975-642.652-368.63-890.172-644.29l364.0172248-384.86955c202.2047752 224.44055 449.7907752 403.33155 726.3497752 524.81755 276.563 121.486 575.773 182.791 877.813 179.853 197.66.263 394.34-27.707 584.09-83.064l138.41 527.463c-233.15 66.62-474.49 100.17-716.96 99.68" /></svg>
                    : <svg className="block__swoosh" height="1259" viewBox="0 0 2337 1259" width="2337" xmlns="http://www.w3.org/2000/svg"><path d="m734.2 5.25018c370.41-4.24759 737.46 70.61612 1076.63 219.58982 339.17 148.975 642.65 368.63 890.17 644.29l-364.02 384.87c-202.2-224.44-449.79-403.332-726.35-524.818s-575.77-182.791-877.813-179.853c-197.656-.263-394.338 27.707-584.088 83.064l-138.4097-527.464c233.1467-66.6179 474.4897-100.17159 716.9627-99.67882"/></svg>
                  }
                </>
              : <>
                <SharedBlockLayout.ModernImageSrcSet
                  image={image}
                  lazyload={true}
                  priority={undefined}
                  classNames={getClassNameModern(imageTextBlockClasses, 'image', 'w-100')}
                  pictureClassNames='block__picture'
                  imageProportions={{
                    width: '866',
                    height: '487',
                    sizes: '(min-width: 960px) 433px, (min-width: 768px) 48vw, 100vw'
                  }}
                />
              </>
            }
          </div>
        )}
      </>
    )
  },
}
